import React, { useEffect, useRef, useState } from "react";

interface IProps {
  /** Appelé à chaque modification du tracé : base64 PNG, ou null si vidé */
  onChange: (dataUrl: string | null) => void;
  height?: number;
}

/**
 * Zone de signature tactile/souris.
 * Le tracé est exporté en PNG base64 (data:image/png;base64,...).
 */
export const SignaturePad: React.FC<IProps> = ({ onChange, height = 180 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const [isEmpty, setIsEmpty] = useState(true);

  // Adapte la résolution interne du canvas à sa taille affichée (écrans HiDPI)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const { width } = canvas.getBoundingClientRect();
      canvas.width = width * ratio;
      canvas.height = height * ratio;

      const context = canvas.getContext("2d");
      if (context) {
        context.scale(ratio, ratio);
        context.lineWidth = 2;
        context.lineCap = "round";
        context.lineJoin = "round";
        context.strokeStyle = "#111827";
      }
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [height]);

  const getPosition = (event: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();

    if ("touches" in event) {
      const touch = event.touches[0];
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event: React.MouseEvent | React.TouchEvent) => {
    const context = canvasRef.current?.getContext("2d");
    if (!context) return;
    drawingRef.current = true;
    const { x, y } = getPosition(event);
    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (event: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current) return;
    // Empêche le scroll de la page pendant la signature au doigt
    event.preventDefault();
    const context = canvasRef.current?.getContext("2d");
    if (!context) return;
    const { x, y } = getPosition(event);
    context.lineTo(x, y);
    context.stroke();
  };

  const stop = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsEmpty(false);
    onChange(canvas.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
    onChange(null);
  };

  return (
    <div className="w-full">
      <div className="relative">
        <canvas
          ref={canvasRef}
          style={{ height }}
          className="w-full border border-gray-300 dark:border-zinc-700 rounded-md bg-white touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={stop}
        />
        {isEmpty && (
          <span className="absolute inset-0 flex items-center justify-center text-sm text-gray-400 pointer-events-none">
            Signez ici
          </span>
        )}
      </div>

      <div className="flex justify-end mt-2 print:hidden">
        <button
          type="button"
          onClick={clear}
          className="text-sm text-gray-500 dark:text-zinc-400 hover:underline"
        >
          Effacer
        </button>
      </div>
    </div>
  );
};
